/**
 * Infinite Scroll Hook
 * Detects when user scrolls near bottom and triggers load more callback
 */

import { useCallback, useEffect, useRef } from "react"
import { API } from "@/constants/constants"

export interface UseInfiniteScrollOptions {
  onLoadMore: () => void | Promise<void>
  hasMore: boolean
  threshold?: number
  disabled?: boolean
}

export function useInfiniteScroll(options: UseInfiniteScrollOptions) {
  const {
    onLoadMore,
    hasMore,
    threshold = API.INFINITE_SCROLL_THRESHOLD_PX,
    disabled = false,
  } = options
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0]
      if (target.isIntersecting && hasMore && !disabled) {
        onLoadMore()
      }
    },
    [hasMore, disabled, onLoadMore],
  )

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: `${threshold}px`,
      threshold: 0,
    }

    observerRef.current = new IntersectionObserver(handleObserver, observerOptions)

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observerRef.current.observe(currentRef)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [handleObserver, threshold])

  return loadMoreRef
}
